import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, OnDestroy, inject, Input } from '@angular/core';
import { CurrentStateService, DirtyFlagService } from '@ddv/behaviors';
import { appWidgetStateToConfig } from '@ddv/dashboards';
import { FeatureFlagService, UserData, UserEntitlements } from '@ddv/entitlements';
import { ErrorResponseDetail } from '@ddv/http';
import { AppLayoutState, createWorkspaceObj, Workspace, LayoutService, TopLevelWorkspaceComponent, ManagerService } from '@ddv/layout';
import {
    AdditionalFilter,
    AppWidgetState,
    Client,
    MANAGE_WIDGET_WS_ID,
    MANAGE_WIDGET_WS_KEY,
    MODE,
    NamedQuery,
    UserPreferences,
    WIDGET_LIFECYCLE_EVENT,
} from '@ddv/models';
import { NamedQueriesService } from '@ddv/named-queries';
import { InitializationErrorCollectorService } from '@ddv/navigation';
import { deepClone } from '@ddv/utils';
import { LegacyWidgetsService, removeTradeReleaseComment, WidgetsService } from '@ddv/widgets';
import { FuzzyDate } from '@hs/ui-core-date';
import { Fund } from '@hs/ui-core-fund';
import { forkJoin, of, Subscription, switchMap, take, tap } from 'rxjs';

import { WS_DEFINITION } from '../../models/workspace.configuration';
import { MWLeftNavComponent } from '../left-nav/manage-widgets-left-nav/manage-widgets-left-nav.component';
import { ManageWidgetsDashboardComponent } from '../manage-widgets-dashboard/manage-widgets-dashboard.component';

@Component({
    templateUrl: 'manage-widgets.component.html',
    styleUrls: ['manage-widgets.component.scss'],
    standalone: false,
})
export class ManageWidgetsComponent implements OnInit, OnDestroy, TopLevelWorkspaceComponent {
    @Input() entitlements: UserEntitlements | undefined;
    @Input() user: UserData | undefined;
    @Input() clients: Client[] | undefined;
    @Input() fuzzyDates: FuzzyDate[] | undefined;
    @Input() funds: Fund[] = [];
    @Input() preferences: UserPreferences | undefined;

    @ViewChild('leftNavComponent', { static: false }) mwNavComponent: MWLeftNavComponent | undefined;
    @ViewChild('gridcanvas', { static: true }) gridcanvas: ElementRef | undefined;
    @ViewChild('dashboardContainer', { static: true }) dashboardContainer: ElementRef | undefined;
    @ViewChild('dummyDashboardDiv', { static: true }) dummyDashboardDiv: ElementRef | undefined;
    @ViewChild('dashboardComponent', { static: false }) private readonly manageWidgetDashboardRef: ManageWidgetsDashboardComponent | undefined;

    private readonly cdr = inject(ChangeDetectorRef);
    private readonly layoutService = inject(LayoutService);
    private readonly dirtyFlagService = inject(DirtyFlagService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly currentStateService = inject(CurrentStateService);
    private readonly initializationErrorsService = inject(InitializationErrorCollectorService);
    private readonly legacyWidgetsService = inject(LegacyWidgetsService);
    private readonly manager = inject(ManagerService);
    private readonly namedQueryService = inject(NamedQueriesService);
    private readonly widgetsService = inject(WidgetsService);

    currentDashboard: Workspace | undefined;
    isDashboardLoading = true;
    namedQuery: NamedQuery | undefined;
    autoAddDynamicColumnsVisualizations: Map<string, boolean> | undefined;

    protected clientCode: string | undefined;
    protected hasPermission = false;
    protected initializationErrors: { message: string, error: ErrorResponseDetail }[] = [];
    protected currentWidgetId: number | undefined;

    private subscriptions: Subscription[] = [];
    private isMultiClient = false;
    private doesMWExist = false;

    ngOnDestroy(): void {
        this.manager.removeWorkspace();

        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions = [];
    }

    ngOnInit(): void {
        this.initializationErrorsService.errors$
            .subscribe({
                next: (errors) => this.initializationErrors = errors,
            });

        this.layoutService.setTopLevelWorkspaceComponent(this);
        this.layoutService.initializeLayoutManager();
        this.manager.registerWorkspaceStateChangeHandler(this.onWorkspaceChangeEvent.bind(this));

        this.subscriptions.push(this.currentStateService.clientCode$
            .subscribe({
                next: (clientCode) => this.clientCode = clientCode,
            }));

        forkJoin([
            this.currentStateService.currentWidgetId$.pipe(take(1)),
            this.currentStateService.isMultiClient$.pipe(take(1)),
        ]).subscribe({
            next: ([_, isMultiClient]) => {
                this.isMultiClient = isMultiClient;
                this.setUserEntitlements(this.entitlements);

                const currentWidgetId = this.currentStateService.currentWidgetId$.subscribe({
                    next: (widgetId) => this.respondToWidgetIdChange(widgetId),
                });
                this.subscriptions.push(currentWidgetId);
            },
        });
    }

    onRevertChanges(widgetId: number): void {
        this.fetchWidgetInfo(widgetId);
    }

    onUpdateNamedQuery(namedQuery: NamedQuery): void {
        this.namedQuery = namedQuery;
    }

    onUpdateAutoAddDynamicColumnsVisualizations(visualizations: Map<string, boolean>): void {
        // We need a new map every time in order to trigger the change detection in the child component since:
        // The default change-detection algorithm looks for differences
        // by comparing bound-property values by reference across change detection runs
        // https://angular.io/api/core/DoCheck
        this.autoAddDynamicColumnsVisualizations = deepClone(visualizations);
    }

    onWorkspaceChangeEvent(appLayoutState: AppLayoutState, isWorkspaceRendered: boolean, eventName: string): void {
        if (this.isDirtyOnWorkspaceChange(isWorkspaceRendered, eventName)) {
            this.dirtyFlagService.enterDirtyState(this.manager.getCurrentDashboardId() ?? '');
        }
    }

    onTransitionEnd(): void {
        if (this.manager.getWorkspace()) {
            this.updateGridSize();
        }
    }

    private respondToWidgetIdChange(widgetId: number | null): void {
        if (widgetId == null) {
            return this.initializeForWidget({} as AppWidgetState);
        }

        this.currentWidgetId = widgetId;
        this.fetchWidgetInfo(widgetId);
    }

    private fetchWidgetInfo(widgetId: number): void {
        let widgetInfo: AppWidgetState | undefined;

        this.subscriptions.push(this.featureFlagService.isFlagEnabled('use-aws-for-widgets', this.clientCode ?? '')
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        return this.widgetsService.fetchWidgetInfo(widgetId, this.isMultiClient, this.clientCode ?? '');
                    }

                    if (this.isMultiClient) {
                        return this.legacyWidgetsService.fetchWidgetInfoForMultiClient(widgetId);
                    }

                    return this.legacyWidgetsService.fetchWidgetInfo(widgetId);
                }),
                tap((appWidgetState: AppWidgetState) => widgetInfo = appWidgetState),
                switchMap((appWidgetState: AppWidgetState) => {
                    if (appWidgetState.namedQueryId) {
                        return this.namedQueryService.fetchNamedQuery(appWidgetState.namedQueryId);
                    }
                    return of(undefined);
                }),
            ).subscribe({
                next: (namedQuery: NamedQuery | undefined) => {
                    if (namedQuery) {
                        this.namedQuery = namedQuery;
                    }
                    if (!this.isMultiClient) {
                        widgetInfo?.widgetFilters?.filters?.forEach((filter) => {
                            this.applyWidgetFilter(filter);
                        });
                    }
                    this.cdr.detectChanges();
                    if (widgetInfo) {
                        removeTradeReleaseComment(widgetInfo, this.entitlements?.getCostCenter());
                    }
                    this.initializeForWidget(widgetInfo!);
                },
            }));
    }

    private applyWidgetFilter(filter: AdditionalFilter): void {
        if (filter.filterValuesType === 'number') {
            filter.values = filter.values?.map((value) => Number(value));
        }

        if (filter.filterValuesType === 'boolean') {
            filter.values = filter.values?.map((value) => value != null ? value === 'true' : value);
        }
    }

    private initializeForWidget(widgetInfo: AppWidgetState): void {
        if (widgetInfo.id) {
            this.mwNavComponent?.onEditBaseWidget(widgetInfo);
        }
        this.registerAppModel();
    }

    private setUserEntitlements(userEntitlements: UserEntitlements | undefined): void {
        if (userEntitlements?.hasNoPermissions()) {
            throw new Error('You are not entitled to anything');
        }
        this.hasPermission = true;
        this.cdr.detectChanges();

        if (this.mwNavComponent) {
            this.manageWidgetDashboardRef?.setNavRef(this.mwNavComponent);
        }
    }

    private showWorkspaceById(wsId: string, wsKey: string): void {
        if (this.manager.getWorkspace()?.id !== wsId) {
            this.addWorkspaceAndSwitch(wsKey);
        } else {
            this.doesMWExist = true;
        }
        this.updateCurrentDashboard(wsId);
        this.layoutService.resizeDashboardAndWidgets();
    }

    private updateCurrentDashboard(wsId: string): void {
        this.currentDashboard = wsId ? this.manager.getWorkspace() : undefined;
    }

    private addWorkspaceAndSwitch(id: string): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dashboard = createWorkspaceObj((WS_DEFINITION as any)[id]);
        this.manager.addWorkspace(dashboard);
        const dashboardModel = dashboard.getExtraParameters();
        this.manager.createWidgets(appWidgetStateToConfig(dashboardModel?.widgets, this.layoutService.getWorkspaceMode()));
    }

    private isDirtyOnWorkspaceChange(isWorkspaceRendered: boolean, eventName: string): boolean {
        return isWorkspaceRendered &&
            eventName !== WIDGET_LIFECYCLE_EVENT.INIT_WIDGET &&
            eventName !== WIDGET_LIFECYCLE_EVENT.AFTER_CASCADE &&
            eventName !== WIDGET_LIFECYCLE_EVENT.AFTER_MAXIMIZE;
    }

    private registerAppModel(): void {
        // Note, widgetInfo is not being used here, the default partial/fake dashboardModel.widgets
        // is actually registered in the shared service.
        // widgetInfo comes directly from the MW API and contains actual ID (not 9999) and some other
        // things that may be necessary to tell the application "this is a live demo" but some information
        // is lost by not registering the original widgetInfo
        if (this.manager.getWorkspace()?.id !== MANAGE_WIDGET_WS_ID) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dashboard = createWorkspaceObj(WS_DEFINITION.MANAGE_WIDGET as any);
            this.manager.addWorkspace(dashboard);
            const dashboardModel = dashboard.getExtraParameters();
            this.manager.createWidgets(appWidgetStateToConfig(dashboardModel?.widgets, this.layoutService.getWorkspaceMode()));
            this.cdr.detectChanges();
        } else {
            this.doesMWExist = true;
            this.layoutService.updateLayout();
        }
        this.currentDashboard = this.manager.getWorkspace();

        this.layoutService.resizeDashboardAndWidgets();
        if (this.doesMWExist) {
            this.layoutService.updateWidgetBehaviour(true);
        }
        this.isDashboardLoading = false;

        this.showWorkspaceById(MANAGE_WIDGET_WS_ID, MANAGE_WIDGET_WS_KEY);
        this.layoutService.changeWorkspaceMode(MODE.EDIT_WORKSPACE);
        this.updateGridSize();
    }

    private updateGridSize(): void {
        const layoutHandler = this.manager.getWorkspaceLayoutHandler();
        setTimeout(() => layoutHandler?.onWindowResize(undefined, true), 500);
    }
}
